import { useState } from "react";
import { Eye, EyeOff, Lock, Mail, Shield } from "lucide-react";
import { encrypt } from "../../config/aes";

import { useNavigate } from "react-router-dom";
import useToken from "../../hooks/auth/useToken";
import { authService } from "../../services/authService";

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { saveToken } = useToken();
  const navigate = useNavigate();

  const handleSubmit = async () => {
    setIsLoading(true);

    try {
      const secureEmail = encrypt(email);
      const securePassword = encrypt(password);
      const response = await authService.login(secureEmail, securePassword);

      // Backend renvoie: { statusCode: 200, message: "...", data: { token: "..." } }
      if (response?.statusCode === 200 && response?.data?.token) {
        console.log("Login successful:", response);
        saveToken(response.data.token);

        localStorage.setItem("token", response.data.token);

        navigate(`/redirect?token=${response.data.token}`);
      } else {
        console.error("Login failed:", response?.message || "No token received");
        alert(response?.message || "Échec de la connexion. Vérifiez vos identifiants.");
      }
    } catch (error: any) {
      console.error("Login error:", error);
      const errorMessage = error?.response?.data?.message || "Erreur de connexion. Veuillez réessayer.";
      alert(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex justify-center items-center px-4 min-h-screen bg-gradient-to-br from-green-50 via-white to-green-50 sm:px-6 lg:px-8">
      <div className="space-y-8 w-full max-w-md">
        {/* Header */}
        <div className="text-center">
          <div className="flex justify-center items-center mx-auto w-30 h-30 ">
            <img src="/logo.png" alt="" />
          </div>
          <h2 className="mt-6 text-3xl font-bold text-gray-900">IZILIFE PAY</h2>
          <p className="mt-2 text-sm text-gray-600">Tableau de bord du Service client</p>
          <div className="mx-auto mt-1 w-12 h-1 bg-gradient-to-r from-green-500 to-green-600 rounded-full"></div>
        </div>

        {/* Form */}
        <div className="p-8 rounded-2xl border border-green-100 shadow-xl backdrop-blur-sm bg-white/70">
          <div className="space-y-6">
            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block mb-2 text-sm font-medium text-gray-700">
                Adresse email
              </label>
              <div className="relative">
                <div className="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
                  <Mail className="w-5 h-5 text-green-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="block py-3 pr-3 pl-10 w-full placeholder-gray-400 text-gray-900 rounded-xl border border-gray-200 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white/50"
                  placeholder="admin@izilifepay.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block mb-2 text-sm font-medium text-gray-700">
                Mot de passe
              </label>
              <div className="relative">
                <div className="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
                  <Lock className="w-5 h-5 text-green-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  className="block py-3 pr-10 pl-10 w-full placeholder-gray-400 text-gray-900 rounded-xl border border-gray-200 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white/50"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="flex absolute inset-y-0 right-0 items-center pr-3"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5 text-gray-400 transition-colors hover:text-green-500" />
                  ) : (
                    <Eye className="w-5 h-5 text-gray-400 transition-colors hover:text-green-500" />
                  )}
                </button>
              </div>
            </div>

            {/* Remember Me & Forgot Password */}
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  className="w-4 h-4 text-green-600 rounded border-gray-300 focus:ring-green-500"
                />
                <label htmlFor="remember-me" className="block ml-2 text-sm text-gray-700">
                  Se souvenir de moi
                </label>
              </div>

              <div className="text-sm">
                <a href="#" className="font-medium text-green-600 transition-colors hover:text-green-500">
                  Mot de passe oublié ?
                </a>
              </div>
            </div>

            {/* Submit Button */}
            <div>
              <button
                onClick={handleSubmit}
                disabled={isLoading}
                className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-xl text-white bg-gradient-to-r from-green-600 to-green-500 hover:from-green-700 hover:to-green-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <div className="mr-2 w-4 h-4 rounded-full border-b-2 border-white animate-spin"></div>
                    Connexion en cours...
                  </div>
                ) : (
                  "Se connecter"
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center">
          <p className="text-xs text-gray-500">© 2025 IZILIFE PAY. Tous droits réservés.</p>
          <p className="mt-1 text-xs text-gray-400">Accès sécurisé au tableau de bord du servic client</p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
